import React from 'react';

/**
 * THE GANTT DATE AXIS — the row of date cells above a Gantt timeline, shared by
 * the Breakdown timeline (`gags/GagTimelineView.js`) and the Crew Gantt
 * (`budget/CrewGanttView.js`). Extracted 2026-09-23 (owner: the Crew Gantt's
 * sticky date band should share code with the Breakdown's) from two
 * byte-identical copies of the cell markup.
 *
 * 🔑 WHAT IT OWNS, AND WHAT IT DELIBERATELY DOES NOT:
 *   - OWNS the cell markup (one absolutely-positioned, weekend-tinted cell per
 *     `dateHeaders` entry), the band's height, and its OPAQUE surface.
 *   - Does NOT own POSITIONING. Where the axis pins is the CALLER's scroll-model
 *     question and the two callers answer it differently: the Breakdown's
 *     timeline owns BOTH axes, so its axis is `sticky top-0` INSIDE it; the Crew
 *     Gantt's timeline owns `overflow-x` ONLY (a sticky child of an x-only
 *     scroller is INERT — its y computes to `auto` and there is nothing to
 *     pin against), so its axis lives in a band OUTSIDE the timeline. Pass the
 *     positioning classes in `className`.
 *   - Does NOT own `dateHeaders`. Each view still builds its own (the crew copy
 *     carries an `isWeekend` flag the `utils/ganttDates.js` one lacks — a
 *     declared LEGACY_DEBT group in `scripts/duplication-manifest.js`).
 *
 * 🚨 OPAQUE IN BOTH THEMES, AND IT MUST STAY SO. Rows scroll UNDER a pinned
 * axis; a translucent fill (`/70`, `/60`) lets them show through.
 *
 * 🚨 `GANTT_HEADER_HEIGHT` IS SCROLL-SPACE, NOT DECORATION. Both views add it
 * to row offsets (the Breakdown's focus clamp and row→scroll conversion; the
 * Crew Gantt's level-2 panel `top`). Do not raise it to win room for a label.
 *
 * 🪤 `GANTT_AXIS_Z` (`z-30`) sits ABOVE the today marker (`z-20`) and sticky
 * group strips (`z-10`) INSIDE a chart. It is NOT a tier of the page z-index
 * contract (`components/ui/CLAUDE.md`): it is meant to win INSIDE the chart,
 * never against the page's toolbar or app bar — so every chart root that renders
 * a pinned axis carries `isolate` (measured 2026-09-23: without it the axis
 * painted over both on page scroll).
 *
 * jsdom has no layout: whether the axis PINS is a browser check.
 */

// THE height of a Gantt bar in a 42px row (owner 2026-10-13: "the slightly
// thinner bars", then all charts). The Crew Gantt keeps its own 30: its 6th and
// 7th day marks are sized against that bar.
export const GANTT_BAR_HEIGHT = 22;
export const GANTT_HEADER_HEIGHT = 48;
// The class stays a LITERAL so Tailwind's scanner compiles it; the number is
// its twin for arithmetic, and a test pins `GANTT_AXIS_Z === z-${INDEX}`.
export const GANTT_AXIS_Z = 'z-30';
export const GANTT_AXIS_Z_INDEX = 30;
// 🔑 A DRAGGED BAR's tier: above the resting bars, the today marker (z-20) and
// the Crew Gantt's level-2 panel (z-25), and STRICTLY BELOW the axis. It was
// 30 — a tie with the axis that the bar, later in the DOM, won: measured in
// Chromium (review C3, 2026-09-23), a dragged member's day cells painted over
// the month labels. Never raise it to meet the axis.
export const GANTT_DRAG_Z = 27;
// An INLINE DETAILS PANEL laid over a chart's rows (the Crew Gantt's level-2
// panel; the Labour view's gag panel): above the today marker (z-20), below a
// dragged bar and the date strip.
export const GANTT_PANEL_Z = 25;
export const GANTT_AXIS_SURFACE = 'border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800';

/**
 * THE grid behind a Gantt's rows: one column per axis cell (`buildDateHeaders`),
 * so the lines sit under the date strip's own divisions at every zoom, with
 * weekends tinted where the cells are days. Five charts each drew this markup
 * themselves, and the Labour view drew a different one (owner 2026-10-13: "why
 * has the labour view not got the same grid markings and date layout as
 * timeline?", then "make sure this is common code").
 * `height` is the rows' pixel height; omit it and the grid fills its parent.
 */
export function GanttGrid({ dateHeaders, dayWidth, height }) {
  return dateHeaders.map((h, i) => (
    <div
      key={`grid-${i}`}
      className={`absolute top-0 bottom-0 border-r ${h.isWeekend ? 'bg-blue-50/50 dark:bg-blue-900/15' : ''} border-gray-100 dark:border-gray-700`}
      style={{ left: h.offset * dayWidth, width: h.width * dayWidth, height }}
    />
  ));
}

/**
 * THE today line: a dashed red rule with a "Today" tag, drawn only when today
 * is inside the chart's range. `lineRef` is for a chart that measures it.
 * It sits at z-20: above the bars, below a dragged bar and the date strip.
 */
export function GanttTodayLine({ todayOffset, totalDays, dayWidth, height, lineRef }) {
  if (!(todayOffset >= 0 && todayOffset <= totalDays)) return null;
  return (
    <div
      ref={lineRef}
      data-testid="gantt-today-line"
      className={`absolute top-0 z-20 pointer-events-none ${height == null ? 'bottom-0' : ''}`.trim()}
      style={{ left: todayOffset * dayWidth, height, width: 2, borderLeft: '2px dashed #ef4444' }}
    >
      <div className="absolute -top-0 -left-3 bg-red-500 text-white text-[9px] px-1 rounded font-bold">Today</div>
    </div>
  );
}

export default function GanttDateAxis({
  dateHeaders,
  dayWidth,
  className = '',
  style,
  testId,
  height = GANTT_HEADER_HEIGHT,
}) {
  return (
    <div
      data-testid={testId}
      className={`${className} ${GANTT_AXIS_SURFACE}`.trim()}
      style={{ ...style, height }}
    >
      {dateHeaders.map((h, i) => (
        <div
          key={i}
          className={`absolute top-0 flex flex-col items-start justify-center px-1 border-r border-gray-100 dark:border-gray-700 text-xs ${h.isWeekend ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
          style={{ left: h.offset * dayWidth, width: h.width * dayWidth, height }}
        >
          <span className="text-gray-600 dark:text-gray-300 font-medium truncate w-full">{h.label}</span>
          {h.sublabel && <span className="text-gray-400 dark:text-gray-500 text-[10px]">{h.sublabel}</span>}
        </div>
      ))}
    </div>
  );
}
